import * as Sentry from '@sentry/react'
import { useState, useEffect } from 'react'

import { OnboardingGate, useOnboardingGate } from '@/components/OnboardingGate'
import { useTransferWithQuotes, useUserData } from '@/hooks/useCpn'
import { useMaintenanceStatus } from '@/hooks/useMaintenance'
import { authService } from '@/services/auth'
import { cpnService } from '@/services/cpn'
import { CpnBeneficiaryUser } from '@/types/cpn'
import type {
  PendingQuoteData,
  PreparedQuoteData,
  RealSwapQuoteData,
  SwapQuotePayload,
} from '@/types/quote'
import { Screen } from '@/types/screen'
import { MINIMUM_PLATFORM_FEE_CAD } from '@/utils/feeCalculations'
import { logger } from '@/utils/logger'
import {
  DEFAULT_PAYMENT_REASON,
  getPaymentPreference,
} from '@/utils/paymentPreferences'

import SupportTicketModal from './overlays/SupportTicketModal'
import AddBeneficiaryScreen from './screens/AddBeneficiaryScreen'
import AuthLandingScreen from './screens/AuthLandingScreen'
import BankDetailsScreen from './screens/BankDetailsScreen'
import BeneficiariesManagementScreen from './screens/BeneficiariesManagementScreen'
import BeneficiaryEditScreen from './screens/BeneficiaryEditScreen'
import BeneficiaryMetadataScreen from './screens/BeneficiaryMetadataScreen'
import BeneficiarySelectionScreen from './screens/BeneficiarySelectionScreen'
import BeneficiaryViewScreen from './screens/BeneficiaryViewScreen'
import DashboardScreen from './screens/DashboardScreen'
import EmailRecipientScreen from './screens/EmailRecipientScreen'
import PaymentConfirmationScreen from './screens/PaymentConfirmationScreen'
import PaymentDetailsScreen from './screens/PaymentDetailsScreen'
import PaymentListScreen from './screens/PaymentListScreen'
import PaymentStatusScreen from './screens/PaymentStatusScreen'
import PersonalInfoManagementScreen from './screens/PersonalInfoManagementScreen'
import RecipientSelectionScreen from './screens/RecipientSelectionScreen'
import ReviewDetailsScreen from './screens/ReviewDetailsScreen'
import SavedBeneficiariesScreen from './screens/SavedBeneficiariesScreen'
import SendMoneyScreen from './screens/SendMoneyScreen'
import SettingsScreen from './screens/SettingsScreen'
import SignInScreen from './screens/SignInScreen'
import SignUpScreen from './screens/SignUpScreen'
import UploadPaymentDetailsScreen from './screens/UploadPaymentDetailsScreen'

const MoneyTransferWidgetContent = () => {
  const { isAuthenticated, needsOnboarding, refreshAuth } = useOnboardingGate()
  const { isMaintenanceActive } = useMaintenanceStatus('atb')
  const [fromPayByEmail, setFromPayByEmail] = useState(false)
  const [showAuthScreens, setShowAuthScreens] = useState(false)
  const [showSavedBeneficiaries, setShowSavedBeneficiaries] = useState(false)
  const [currentScreen, setCurrentScreen] = useState<Screen>(() => {
    if (!isAuthenticated) return Screen.SEND_MONEY
    if (sessionStorage.getItem('lockedQuoteData')) {
      return Screen.BENEFICIARY_SELECTION
    }
    return Screen.DASHBOARD
  })
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null
  )
  const [currentBeneficiaryId, setCurrentBeneficiaryId] = useState<
    string | null
  >(null)
  const [authScreen, setAuthScreen] = useState<'landing' | 'signin' | 'signup'>(
    'landing'
  )
  const [beneficiaryToEdit, setBeneficiaryToEdit] =
    useState<CpnBeneficiaryUser | null>(null)
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<
    CpnBeneficiaryUser | undefined
  >()

  const userId = authService.getUserId()
  const { data: clarityUserData } = useUserData(isAuthenticated && !!userId)

  useEffect(() => {
    if (!isAuthenticated || !userId || !clarityUserData) return

    try {
      const email = authService.getAuthData()?.email
      window.clarity?.(
        'identify',
        userId,
        undefined,
        undefined,
        `${clarityUserData.firstName} ${clarityUserData.lastName}`
      )
      if (email) window.clarity?.('set', 'email', email)
    } catch (e) {
      Sentry.captureException(e)
    }
  }, [isAuthenticated, userId, clarityUserData])

  // Get user's saved payment preferences or use defaults
  const savedPreference = getPaymentPreference(userId)

  const {
    transferData,
    setTransferData,
    setActiveField,
    quote,
    isQuoteLoading,
    quoteError,
    quoteMode,
    formattedQuote,
  } = useTransferWithQuotes(
    {
      sendAmount: '1000.00',
      sendCurrency: 'CAD',
      receiveAmount: '0.00',
      receiveCurrency: savedPreference.receiveCurrency,
      receiveCountry: savedPreference.receiveCountry,
      paymentMethod: savedPreference.paymentMethod,
      fees: MINIMUM_PLATFORM_FEE_CAD,
      exchangeRate: 1.0,
    },
    {
      enabled: !isMaintenanceActive,
    }
  )

  useEffect(() => {
    if (isMaintenanceActive) {
      setShowAuthScreens(false)
      setCurrentScreen(Screen.SEND_MONEY)
    }
  }, [isMaintenanceActive])

  const handleBackToAuthLanding = () => {
    if (needsOnboarding) {
      setAuthScreen('signup')
      return
    }
    setShowAuthScreens(false)
    setAuthScreen('landing')
  }

  const routeAfterAuth = () => {
    const hasLockedQuote = sessionStorage.getItem('lockedQuoteData')
    setCurrentScreen(
      hasLockedQuote ? Screen.BENEFICIARY_SELECTION : Screen.DASHBOARD
    )
  }

  const handleSignIn = (_token: string) => {
    // Token is already stored in sessionStorage by SignInScreen
    setShowAuthScreens(false)
    setAuthScreen('landing')
    routeAfterAuth()
    refreshAuth()
  }

  const handleSignUpSuccess = (_email: string) => {
    setShowAuthScreens(false)
    setAuthScreen('landing')
    routeAfterAuth()
    refreshAuth()
  }

  const handleInitiateTransfer = async () => {
    if (!formattedQuote) {
      logger.warn('No quote available to lock')
      return
    }

    // Check if user is authenticated first
    if (!isAuthenticated) {
      // Store the quote data to lock after authentication
      const pendingQuoteData: PendingQuoteData = {
        originalQuote: formattedQuote,
        transferData: transferData,
        lockedAt: new Date().toISOString(),
        pendingSwapQuote: true, // Flag to create swap quote after auth
      }
      sessionStorage.setItem(
        'lockedQuoteData',
        JSON.stringify(pendingQuoteData)
      )

      // Prompt login by showing auth landing screen
      setShowAuthScreens(true)
      setAuthScreen('landing')
      return
    }

    try {
      const swapQuotePayload: SwapQuotePayload = {
        leftSideValue: formattedQuote.sourceAmount,
        destinationCountry: transferData.receiveCountry,
        destinationCurrency: transferData.receiveCurrency,
        paymentMethod: transferData.paymentMethod,
      }

      // Store the prepared quote data
      const realQuoteData: PreparedQuoteData = {
        originalQuote: formattedQuote,
        swapQuotePayload,
        lockedAt: new Date().toISOString(),
        needsSwapQuoteCall: true, // Flag to create swap quote in PaymentConfirmationScreen
      }

      sessionStorage.setItem('lockedQuoteData', JSON.stringify(realQuoteData))
      // User is authenticated - proceed to beneficiary selection
      setCurrentScreen(Screen.BENEFICIARY_SELECTION)
    } catch (error) {
      logger.error('Failed to prepare swap quote', error)
      return
    }
  }

  const handleBeneficiarySelected = async (beneficiary: CpnBeneficiaryUser) => {
    setSelectedBeneficiary(beneficiary)

    // NOW we have beneficiary - create the real swap quote!
    try {
      const storedQuoteData = sessionStorage.getItem('lockedQuoteData')
      if (storedQuoteData) {
        const quoteData = JSON.parse(storedQuoteData)

        if (quoteData.needsSwapQuoteCall && quoteData.swapQuotePayload) {
          // Create the real swap quote with beneficiary
          const swapQuotePayload = {
            ...quoteData.swapQuotePayload,
            beneficiaryRefId: beneficiary.uuid,
            ...(beneficiary.isSelf && { isFirstParty: true }),
          }

          const swapQuoteResult =
            await cpnService.createSwapQuote(swapQuotePayload)

          // Store the real locked quote
          const realLockedQuote: RealSwapQuoteData = {
            ...swapQuoteResult,
            originalQuote: quoteData.originalQuote,
            lockedAt: new Date().toISOString(),
            isRealSwapQuote: true,
            swapQuotePayload: swapQuotePayload, // Preserve for future refreshes
          }

          sessionStorage.setItem(
            'lockedQuoteData',
            JSON.stringify(realLockedQuote)
          )
          sessionStorage.setItem('currentIntentId', swapQuoteResult.id)
        }
      }
    } catch (error) {
      logger.error('Failed to create swap quote', error)
      // Continue anyway - we'll handle this in PaymentConfirmationScreen
    }

    // Go to payment confirmation with the beneficiary
    setCurrentScreen(Screen.PAYMENT_CONFIRMATION)
  }

  const handleBeneficiaryCreated = (beneficiaryId: string) => {
    setCurrentBeneficiaryId(beneficiaryId)
    sessionStorage.setItem('currentBeneficiaryId', beneficiaryId)

    // Check if we came from beneficiaries management - if so, go back there
    const fromManagement = sessionStorage.getItem('fromBeneficiariesManagement')
    if (fromManagement) {
      sessionStorage.removeItem('fromBeneficiariesManagement')
      setCurrentScreen(Screen.BENEFICIARIES_MANAGEMENT)
    } else {
      // Go to metadata screen for transfer flow
      setCurrentScreen(Screen.BENEFICIARY_METADATA)
    }
  }

  const handleMetadataComplete = async () => {
    if (!currentBeneficiaryId) {
      logger.warn('No beneficiary ID available')
      setCurrentScreen(Screen.PAYMENT_CONFIRMATION)
      return
    }

    try {
      // Fetch the beneficiary data so we can pass it to PaymentConfirmationScreen
      const response = await cpnService.getBeneficiaryUser(currentBeneficiaryId)
      setSelectedBeneficiary(response.beneficiaryUser)

      setCurrentScreen(Screen.PAYMENT_CONFIRMATION)
    } catch (error) {
      logger.error('Failed to fetch beneficiary data', error)
      // Still proceed but with limited data
      setCurrentScreen(Screen.PAYMENT_CONFIRMATION)
    }
  }

  const handleRecipientSelectionNext = () => {
    if (fromPayByEmail) {
      setCurrentScreen(Screen.EMAIL_RECIPIENT)
    } else {
      setCurrentScreen(Screen.BANK_DETAILS)
    }
  }

  const handleOldBeneficiarySelected = (beneficiary?: CpnBeneficiaryUser) => {
    setSelectedBeneficiary(beneficiary)
    if (beneficiary) {
      // If existing beneficiary selected, go directly to review
      setCurrentScreen(Screen.REVIEW_DETAILS)
    } else {
      // If no beneficiary or adding new, go to bank details
      setCurrentScreen(Screen.BANK_DETAILS)
    }
  }

  const handleBankDetailsNext = (beneficiary?: CpnBeneficiaryUser) => {
    if (beneficiary) {
      setSelectedBeneficiary(beneficiary)
    }
    setCurrentScreen(Screen.REVIEW_DETAILS)
  }

  const handlePaymentComplete = () => {
    setCurrentScreen(Screen.PAYMENT_STATUS)
  }

  const handleStartNewTransfer = () => {
    // Reset all state and go back to start
    setCurrentScreen(Screen.SEND_MONEY)
    setSelectedBeneficiary(undefined)
    setFromPayByEmail(false)
    setShowSavedBeneficiaries(false)
    setSelectedPaymentId(null)
    setCurrentBeneficiaryId(null)
    setBeneficiaryToEdit(null)
    sessionStorage.removeItem('currentQuoteId')
    sessionStorage.removeItem('currentPaymentId')
    sessionStorage.removeItem('currentBeneficiaryId')
  }

  const handleViewPaymentDetails = (paymentId: string) => {
    setSelectedPaymentId(paymentId)
    setCurrentScreen(Screen.PAYMENT_DETAILS)
  }

  const handleEditBeneficiary = (beneficiary: CpnBeneficiaryUser) => {
    setBeneficiaryToEdit(beneficiary)
    setCurrentScreen(Screen.BENEFICIARY_VIEW)
  }

  const handleBeneficiaryEditSave = () => {
    setBeneficiaryToEdit(null)
    setCurrentScreen(Screen.BENEFICIARIES_MANAGEMENT)
  }

  const handleAddBeneficiaryFromManagement = () => {
    sessionStorage.setItem('fromBeneficiariesManagement', 'true')
    setCurrentScreen(Screen.ADD_BENEFICIARY)
  }

  const screens = [
    <SendMoneyScreen
      key="send"
      transferData={transferData}
      setTransferData={setTransferData}
      setActiveField={setActiveField}
      onNext={() => setCurrentScreen(Screen.BENEFICIARY_SELECTION)}
      onInitiateTransfer={handleInitiateTransfer}
      onBack={() => setCurrentScreen(Screen.DASHBOARD)}
      onShowAuth={() => {
        setShowAuthScreens(true)
        setAuthScreen('landing')
      }}
      isQuoteLoading={isQuoteLoading}
      quoteError={quoteError}
      quoteMode={quoteMode}
      formattedQuote={formattedQuote}
    />,
    <DashboardScreen
      key="dashboard"
      onStartPayment={() => setCurrentScreen(Screen.SEND_MONEY)}
      onCheckPaymentStatus={() => setCurrentScreen(Screen.PAYMENT_LIST)}
      onViewAllPayments={() => setCurrentScreen(Screen.PAYMENT_LIST)}
      onViewPaymentDetails={handleViewPaymentDetails}
      onManageBeneficiaries={() =>
        setCurrentScreen(Screen.BENEFICIARIES_MANAGEMENT)
      }
      onManagePersonalInfo={() =>
        setCurrentScreen(Screen.PERSONAL_INFO_MANAGEMENT)
      }
      onOpenSettings={() => setCurrentScreen(Screen.SETTINGS)}
      cadUsdcRate={formattedQuote?.cadUsdcRate}
    />,
    <div key="placeholder" className="p-4"></div>,
    <BeneficiarySelectionScreen
      key="beneficiarySelection"
      transferData={transferData}
      onBeneficiarySelected={handleBeneficiarySelected}
      onAddBeneficiary={() => setCurrentScreen(Screen.ADD_BENEFICIARY)}
      onBack={() => setCurrentScreen(Screen.SEND_MONEY)}
      userId={userId}
    />,
    <AddBeneficiaryScreen
      key="addBeneficiary"
      transferData={
        sessionStorage.getItem('fromBeneficiariesManagement')
          ? undefined
          : transferData
      }
      onNext={handleBeneficiaryCreated}
      onBack={() => {
        // Check if we came from beneficiaries management or regular flow
        const fromManagement = sessionStorage.getItem(
          'fromBeneficiariesManagement'
        )
        if (fromManagement) {
          sessionStorage.removeItem('fromBeneficiariesManagement')
          setCurrentScreen(Screen.BENEFICIARIES_MANAGEMENT)
        } else {
          setCurrentScreen(Screen.BENEFICIARY_SELECTION)
        }
      }}
      userId={userId}
    />,
    <PaymentConfirmationScreen
      key="paymentConfirmation"
      transferData={transferData}
      beneficiary={selectedBeneficiary!}
      onBack={() => setCurrentScreen(Screen.BENEFICIARY_SELECTION)}
      onPaymentSuccess={() => setCurrentScreen(Screen.PAYMENT_STATUS)}
      userId={userId}
      quote={quote}
      formattedQuote={formattedQuote}
    />,
    // Screen 6: Either RecipientSelectionScreen or SavedBeneficiariesScreen
    showSavedBeneficiaries ? (
      <SavedBeneficiariesScreen
        key="savedBeneficiaries"
        onNext={handleOldBeneficiarySelected}
        onBack={() => setCurrentScreen(Screen.ADD_BENEFICIARY)}
        onAddNew={() => setCurrentScreen(Screen.BANK_DETAILS)}
        userId={userId}
      />
    ) : (
      <RecipientSelectionScreen
        key="selection"
        onNext={handleRecipientSelectionNext}
        onBack={() => setCurrentScreen(Screen.ADD_BENEFICIARY)}
        fromPayByEmail={fromPayByEmail}
      />
    ),
    <BankDetailsScreen
      key="bankDetails"
      onNext={handleBankDetailsNext}
      onBack={() =>
        setCurrentScreen(
          showSavedBeneficiaries
            ? Screen.RECIPIENT_SELECTION
            : Screen.ADD_BENEFICIARY
        )
      }
      userId={userId}
      existingBeneficiary={selectedBeneficiary}
      paymentReason={
        selectedBeneficiary?.paymentReason ?? DEFAULT_PAYMENT_REASON
      }
    />,
    <UploadPaymentDetailsScreen
      key="upload"
      onNext={handlePaymentComplete}
      onBack={() => setCurrentScreen(Screen.ADD_BENEFICIARY)}
    />,
    <EmailRecipientScreen
      key="email"
      transferData={transferData}
      onNext={handlePaymentComplete}
      onBack={() =>
        setCurrentScreen(
          fromPayByEmail ? Screen.RECIPIENT_SELECTION : Screen.ADD_BENEFICIARY
        )
      }
    />,
    <ReviewDetailsScreen
      key="review"
      transferData={transferData}
      onNext={handlePaymentComplete}
      onBack={() =>
        setCurrentScreen(
          selectedBeneficiary ? Screen.RECIPIENT_SELECTION : Screen.BANK_DETAILS
        )
      }
      onChangePaymentMethod={() => setCurrentScreen(Screen.SEND_MONEY)}
      onChangeTransferDetails={() => setCurrentScreen(Screen.SEND_MONEY)}
      onChangeRecipientDetails={() => setCurrentScreen(Screen.BANK_DETAILS)}
      onChangeScheduleDetails={() => setCurrentScreen(Screen.SEND_MONEY)}
      beneficiary={selectedBeneficiary}
      userId={userId}
    />,
    <PaymentStatusScreen
      key="paymentStatus"
      transferData={transferData}
      beneficiary={selectedBeneficiary}
      onBack={() => setCurrentScreen(Screen.DASHBOARD)}
      onStartNewTransfer={handleStartNewTransfer}
    />,
    <PaymentListScreen
      key="paymentList"
      onBack={() => setCurrentScreen(Screen.DASHBOARD)}
      onViewPaymentDetails={handleViewPaymentDetails}
    />,
    <PaymentDetailsScreen
      key="paymentDetails"
      paymentId={selectedPaymentId}
      onBack={() => setCurrentScreen(Screen.PAYMENT_LIST)}
      cadUsdcRate={formattedQuote?.cadUsdcRate}
    />,
    <BeneficiaryMetadataScreen
      key="beneficiaryMetadata"
      transferData={transferData}
      beneficiaryId={currentBeneficiaryId}
      onNext={handleMetadataComplete}
      onBack={() => setCurrentScreen(Screen.ADD_BENEFICIARY)}
    />,
    <BeneficiariesManagementScreen
      key="beneficiariesManagement"
      onBack={() => setCurrentScreen(Screen.DASHBOARD)}
      onEditBeneficiary={handleEditBeneficiary}
      onAddBeneficiary={handleAddBeneficiaryFromManagement}
      userId={userId}
    />,
    <BeneficiaryEditScreen
      key="beneficiaryEdit"
      beneficiary={beneficiaryToEdit!}
      onBack={() => setCurrentScreen(Screen.BENEFICIARIES_MANAGEMENT)}
      onSave={handleBeneficiaryEditSave}
    />,
    <BeneficiaryViewScreen
      key="beneficiaryView"
      beneficiary={beneficiaryToEdit!}
      onBack={() => setCurrentScreen(Screen.BENEFICIARIES_MANAGEMENT)}
    />,
    <SettingsScreen
      key="settings"
      onBack={() => setCurrentScreen(Screen.DASHBOARD)}
    />,
    <SupportTicketModal
      key="supportTicket"
      paymentId={'general'}
      onClose={() => setCurrentScreen(Screen.DASHBOARD)}
      onBack={() => setCurrentScreen(Screen.DASHBOARD)}
    />,
    <PersonalInfoManagementScreen
      key="personalInfo"
      onBack={() => setCurrentScreen(Screen.DASHBOARD)}
    />,
  ]

  // Auth screens: voluntary login, or forced when requirements are still pending
  if (needsOnboarding || showAuthScreens) {
    const activeAuthScreen = needsOnboarding
      ? authScreen === 'signin'
        ? 'signin'
        : 'signup'
      : authScreen

    return (
      <div className="w-full max-w-md mx-auto bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden relative max-h-screen">
        {activeAuthScreen === 'landing' && (
          <AuthLandingScreen
            onSignIn={() => setAuthScreen('signin')}
            onSignUp={() => setAuthScreen('signup')}
            onBack={handleBackToAuthLanding}
          />
        )}
        {activeAuthScreen === 'signin' && (
          <SignInScreen
            onBack={handleBackToAuthLanding}
            onSignIn={handleSignIn}
            onSignUp={() => setAuthScreen('signup')}
          />
        )}
        {activeAuthScreen === 'signup' && (
          <SignUpScreen
            onBack={handleBackToAuthLanding}
            onSignUpSuccess={handleSignUpSuccess}
            onSignIn={() => setAuthScreen('signin')}
          />
        )}
      </div>
    )
  }

  return (
    <div className="w-full max-w-md mx-auto bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden relative max-h-screen">
      {screens[currentScreen]}
    </div>
  )
}

const MoneyTransferWidget = () => (
  <OnboardingGate>
    <MoneyTransferWidgetContent />
  </OnboardingGate>
)

export default MoneyTransferWidget
